<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const isExecuting = ref(false);

const ICON_MAP = [
  { match: 'boleto', icon: 'credit-card', color: 'teal' },
  { match: 'diagnosticar', icon: 'eye', color: 'blue' },
  { match: 'reiniciar', icon: 'refresh', color: 'purple' },
  { match: 'encerrar', icon: 'check-circle', color: 'green' },
  { match: 'confiança', icon: 'shield', color: 'yellow' },
  { match: 'desbloqueio manual', icon: 'key', color: 'orange' },
  { match: 'desbloquear', icon: 'unlock', color: 'yellow' },
];

const resolved = computed(() => {
  const name = (props.macro.name || '').toLowerCase();
  const found = ICON_MAP.find(i => name.includes(i.match));
  return found || { icon: 'zap', color: 'blue' };
});

const iconClass = computed(() => 'macro-grid-icon--' + resolved.value.color);

const executeMacro = async () => {
  try {
    isExecuting.value = true;
    await store.dispatch('macros/execute', {
      macroId: props.macro.id,
      conversationIds: [props.conversationId],
    });
    useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
    useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  } finally {
    isExecuting.value = false;
  }
};
</script>

<template>
  <button
    class="macro-grid-btn"
    :class="{ 'macro-grid-btn--loading': isExecuting }"
    :title="macro.name"
    :disabled="isExecuting"
    @click="executeMacro"
  >
    <span class="macro-grid-icon" :class="iconClass">
      <!-- credit-card -->
      <svg
        v-if="resolved.icon === 'credit-card'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
      <!-- eye -->
      <svg
        v-else-if="resolved.icon === 'eye'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      <!-- unlock -->
      <svg
        v-else-if="resolved.icon === 'unlock'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 019.9-1" />
      </svg>
      <!-- refresh -->
      <svg
        v-else-if="resolved.icon === 'refresh'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
      </svg>
      <!-- check-circle -->
      <svg
        v-else-if="resolved.icon === 'check-circle'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
      <!-- shield -->
      <svg
        v-else-if="resolved.icon === 'shield'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      <!-- key -->
      <svg
        v-else-if="resolved.icon === 'key'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"
        />
      </svg>
      <!-- fallback: zap -->
      <svg
        v-else
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    </span>
    <span class="macro-grid-label">{{ macro.name }}</span>
    <span v-if="isExecuting" class="macro-grid-spinner" />
  </button>
</template>

<style scoped lang="scss">
.macro-grid-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border-radius: 8px;
  border: 1px solid var(--s-100);
  background: var(--s-50);
  color: var(--s-900);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
  text-align: center;
  line-height: 1.3;
  font-family: inherit;
  min-height: 0;

  &:hover:not(:disabled) {
    background: var(--s-75);
    border-color: var(--s-200);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--loading {
    position: relative;
  }
}

body.dark .macro-grid-btn {
  border-color: #2a3650;
  background: #1a2332;
  color: #f0f2f7;

  &:hover:not(:disabled) {
    background: #232f42;
    border-color: #3d4f6a;
  }
}

.macro-grid-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &--teal {
    background: rgba(0, 212, 170, 0.12);
    color: #00d4aa;
  }

  &--blue {
    background: rgba(74, 108, 247, 0.12);
    color: #4a6cf7;
  }

  &--yellow {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
  }

  &--purple {
    background: rgba(110, 86, 207, 0.12);
    color: #6e56cf;
  }

  &--green {
    background: rgba(16, 185, 129, 0.12);
    color: #10b981;
  }

  &--orange {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
  }
}

.macro-grid-label {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-width: 100%;
}

.macro-grid-spinner {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(142, 151, 173, 0.3);
  border-top-color: #8e97ad;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
